import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import { useAuth } from '@clerk/clerk-react';
import {
  GET_LIVE_CONTEXT_STATE_QUERY_KEY,
  type LiveContextStateDto,
} from '@/state/queries/player/use-get-live-context-state';
import { entityRoomKeyFromEntityRef, usePerformControlWsStore } from '@/state/player/perform-control-ws.store';

/** Backend expects 'mediaOnly' | 'both' | 'speakerOnly' (LiveContextShowMode values) */
export type StagedSelectionShowMode = 'mediaOnly' | 'both' | 'speakerOnly';

export interface UpdateStagedSelectionRequest {
  entityRef: string;
  stagedSelection: {
    selectedItemIds: string[];
    showMode: StagedSelectionShowMode;
    focusedItemId?: string;
    skippedItemIds?: string[];
    /** Latch Next identity for operator look-ahead showMode overrides. */
    lookAheadForItemId?: string;
    /** Empty selectedItemIds is a real clear. Without this, an empty list is a look-ahead overlay. */
    clearSelection?: boolean;
  };
}

export const useUpdateStagedSelectionMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateStagedSelectionRequest) => {
      const token = await getToken();
      const prev = queryClient.getQueryData<LiveContextStateDto>([GET_LIVE_CONTEXT_STATE_QUERY_KEY, body.entityRef]);
      // Overlay often omits/empties selectedItemIds — keep existing selection so BE does not
      // treat this as a clear-all (which triggers warm preload and clobbers showMode).
      // clearSelection is the explicit empty roster; it is not a look-ahead overlay.
      const clearing = body.stagedSelection.clearSelection === true;
      const selectedItemIds = clearing
        ? body.stagedSelection.selectedItemIds
        : body.stagedSelection.selectedItemIds.length > 0
          ? body.stagedSelection.selectedItemIds
          : (prev?.stagedSelection?.selectedItemIds ?? []);
      const lookAheadOnly =
        !clearing &&
        (body.stagedSelection.lookAheadForItemId != null ||
          (body.stagedSelection.selectedItemIds.length === 0 && body.stagedSelection.skippedItemIds === undefined));
      // Look-ahead overlay: only send showMode + latch (+ preserved selection). Never inject
      // skippedItemIds from cache — a stale [] clears skips on BE and triggers preload clobber.
      return updateStagedSelection(
        {
          ...body,
          stagedSelection: lookAheadOnly
            ? {
                selectedItemIds,
                showMode: body.stagedSelection.showMode,
                lookAheadForItemId:
                  body.stagedSelection.lookAheadForItemId ?? prev?.stagedSelection?.lookAheadForItemId,
              }
            : {
                ...body.stagedSelection,
                selectedItemIds,
                skippedItemIds: body.stagedSelection.skippedItemIds ?? prev?.stagedSelection?.skippedItemIds,
                focusedItemId: body.stagedSelection.focusedItemId ?? prev?.stagedSelection?.focusedItemId,
              },
        },
        token ?? '',
      );
    },
    onMutate: (variables) => {
      const { entityRef, stagedSelection } = variables;
      const prev = queryClient.getQueryData<LiveContextStateDto>([GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef]);
      const clearing = stagedSelection.clearSelection === true;
      const effectiveSelected = clearing
        ? stagedSelection.selectedItemIds
        : stagedSelection.selectedItemIds.length > 0
          ? stagedSelection.selectedItemIds
          : (prev?.stagedSelection?.selectedItemIds ?? []);
      const prevSelected = [...(prev?.stagedSelection?.selectedItemIds ?? [])].sort().join(',');
      const nextSelected = [...effectiveSelected].sort().join(',');
      const lookAheadOnly =
        !clearing &&
        prevSelected === nextSelected &&
        (prev?.stagedSelection?.showMode !== stagedSelection.showMode ||
          (stagedSelection.lookAheadForItemId != null &&
            stagedSelection.lookAheadForItemId !== prev?.stagedSelection?.lookAheadForItemId));
      const optimisticStaged = {
        selectedItemIds: effectiveSelected,
        showMode: stagedSelection.showMode,
        skippedItemIds: stagedSelection.skippedItemIds ?? prev?.stagedSelection?.skippedItemIds,
        focusedItemId: stagedSelection.focusedItemId ?? prev?.stagedSelection?.focusedItemId,
        lookAheadForItemId: stagedSelection.lookAheadForItemId ?? prev?.stagedSelection?.lookAheadForItemId,
      };

      queryClient.setQueryData<LiveContextStateDto>([GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef], (current) => {
        if (!current) {
          return { stagedSelection: optimisticStaged };
        }
        const nextCueState =
          lookAheadOnly && current.cueState
            ? { ...current.cueState, lookAheadShowMode: stagedSelection.showMode }
            : current.cueState;
        return {
          ...current,
          stagedSelection: optimisticStaged,
          cueState: nextCueState,
        };
      });

      // Optimistic WS overlay so merged cueState paints look-ahead immediately.
      // Prefer existing room overlay; fall back to GET cueState when WS has not hydrated yet.
      if (lookAheadOnly) {
        const roomKey = entityRoomKeyFromEntityRef(entityRef);
        if (roomKey) {
          const roomCue = usePerformControlWsStore.getState().cueStateByRoom[roomKey] ?? prev?.cueState;
          if (roomCue) {
            usePerformControlWsStore.setState((state) => ({
              cueStateByRoom: {
                ...state.cueStateByRoom,
                [roomKey]: {
                  ...roomCue,
                  lookAheadShowMode: stagedSelection.showMode,
                },
              },
            }));
          }
        }
      }

      return { prev, entityRef, lookAheadOnly };
    },
    onSuccess: () => {
      // Look-ahead-only: BE emits perform-control WS (no full invalidate).
      // Item/skip changes: BE notifyLiveContextInvalidated → GET refetch.
    },
    onError: (_err, _variables, context) => {
      const ctx = context as { prev?: LiveContextStateDto; entityRef?: string; lookAheadOnly?: boolean } | undefined;
      if (ctx?.entityRef) {
        queryClient.setQueryData([GET_LIVE_CONTEXT_STATE_QUERY_KEY, ctx.entityRef], ctx.prev);
      }
    },
  });
};

const updateStagedSelection = async (
  body: UpdateStagedSelectionRequest,
  token: string,
): Promise<{ success: boolean }> => {
  const url = `${import.meta.env.VITE_API_URL}/player/live-context-state/staged-selection`;
  return sendJSONRequest({ url, method: METHOD.POST, body, token });
};
